import { ThemeEvents } from '@theme/events'

/**
 * Custom element for the PDP Promo Callout block.
 *
 * Listens for variant update events and swaps the inner content
 * with the re-rendered HTML from the section renderer. This means
 * the promo block will appear, disappear, or change automatically
 * when a customer switches variants driven entirely by whether
 * the incoming variant has a matching Promotion ID metafield.
 */
class PdpPromoCallout extends HTMLElement {
	connectedCallback() {
		this._sectionAbort = new AbortController()
		const section = this.closest('.shopify-section, dialog')
		section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, {
			signal: this._sectionAbort.signal,
		})

		// On configurable products the server renders the first available variant's
		// promo data, but the customer hasn't made a selection yet. If the Add to
		// Cart button is disabled (theme sets this when options are unresolved),
		// hide the promo until the customer picks a valid variant combination.
		this._hideIfUnconfigured()

		this.initInteractions()
	}

	/**
	 * Checks whether the product form's submit button is in a disabled /
	 * "Choose Options" state. If so, hides this element immediately.
	 * handleVariantUpdate will re-evaluate visibility once selections are made.
	 */
	_hideIfUnconfigured() {
		const section = this.closest('.shopify-section')
		const submitBtn = section?.querySelector('button[type="submit"][name="add"]')
		if (submitBtn?.disabled) {
			this.style.display = 'none'
		}
	}

	disconnectedCallback() {
		this._sectionAbort?.abort()
	}

	/**
	 * Wire up the See Details button and modal close/backdrop interactions.
	 * Called on first connect and again after inner content is swapped.
	 */
	initInteractions() {
		const inner = this.querySelector('[ref="promoInner"]')
		if (!inner) return

		const dialog = inner.querySelector('.pdp-promo-modal')
		if (!dialog) return

		// Open modal
		const openBtn = inner.querySelector('[data-open-dialog]')
		if (openBtn) {
			openBtn.addEventListener('click', () => dialog.showModal())
		}

		// Close on X button
		const closeBtn = dialog.querySelector('[data-close-dialog]')
		if (closeBtn) {
			closeBtn.addEventListener('click', () => dialog.close())
		}

		// Close on backdrop click
		dialog.addEventListener('click', (e) => {
			if (e.target === dialog) dialog.close()
		})
	}

	/**
	 * Handles a variant update event.
	 * Replaces the inner ref div with the re-rendered version from the
	 * section renderer, then shows or hides the custom element accordingly.
	 *
	 * @param {CustomEvent} event
	 */
	handleVariantUpdate = (event) => {
		// If a product swap occurred, update our tracked product ID
		if (event.detail.data.newProduct) {
			this.dataset.productId = event.detail.data.newProduct.id
		} else if (event.target instanceof HTMLElement && event.target.dataset.productId != null && event.target.dataset.productId !== this.dataset.productId) {
			return
		}

		const newInner = event.detail.data.html?.querySelector('pdp-promo-callout [ref="promoInner"]')
		const currentInner = this.querySelector('[ref="promoInner"]')

		if (!currentInner) return

		if (!newInner) {
			this.style.display = 'none'
			return
		}

		currentInner.replaceWith(newInner)

		// Show or hide based on whether the new inner has content
		this.style.display = newInner.children.length ? '' : 'none'

		// Re-attach interactions to the newly inserted content
		this.initInteractions()
	}
}

if (!customElements.get('pdp-promo-callout')) {
	customElements.define('pdp-promo-callout', PdpPromoCallout)
}
